import { SpeakingLevel } from "../gql/graphql.ts";
import * as yup from "yup";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { getAllSpeakingClassLevelOptions } from "./getAllUserSpeakingLevelOptions.ts";
import { useWithLoading } from "../UI/Loading/useWithLoading.ts";
import { Col } from "../UI/Layout/Col.tsx";
import { Row } from "../UI/Layout/Row.tsx";
import { InputWithLabel } from "../UI/InputWithLabel.tsx";
import { TextInput } from "../UI/TextInput.tsx";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import { BooleanInput } from "../UI/BooleanInput.tsx";
import { Select } from "@normasteaching/norma-ui";
import { useAllUserGroups } from "../UserGroup/useAllUserGroups.ts";
import { useEffect } from "react";
export type UserFormInputs = {
  speakingLevel: SpeakingLevel;
  email: string;
  givenName: string;
  familyName: string;
  userGroupId?: string;
  shouldEnrollToAllPaidContent?: boolean;
  activateAccount?: boolean;
  hideBookSpeakingClassSection?: boolean;
  hideBookProgressCheckSection?: boolean;
  shouldSendResetPasswordEmail?: boolean;
  isOnboardingLiveToBeDone?: boolean;
};

type Props = {
  defaultValues?: UserFormInputs;
  onSave: (data: UserFormInputs) => Promise<void>;
  disabledMap?: {
    [key in keyof UserFormInputs]?: boolean;
  };
  showShouldEnrollToAllPaidContent?: boolean;
  showIsOnboardingLiveToBeDone?: boolean;
};

const schema = yup
  .object({
    speakingLevel: yup
      .mixed<SpeakingLevel>()
      .oneOf(Object.values(SpeakingLevel))
      .required("Specifica un livello di speaking"),
    email: yup
      .string()
      .email("Inserisci un'email valida")
      .required("Inserisci un'email"),
    givenName: yup.string().required("Inserisci un nome"),
    familyName: yup.string().required("Inserisci un cognome"),
    userGroupId: yup.string().optional(),
    hideBookSpeakingClassSection: yup.boolean().optional(),
    hideBookProgressCheckSection: yup.boolean().optional(),
    shouldSendResetPasswordEmail: yup.boolean().optional(),
    shouldEnrollToAllPaidContent: yup.boolean().optional(),
    activateAccount: yup.boolean().optional(),
    isOnboardingLiveToBeDone: yup.boolean().optional(),
  })
  .required();

export function UserForm({
  defaultValues,
  onSave,
  disabledMap,
  showShouldEnrollToAllPaidContent,
  showIsOnboardingLiveToBeDone,
}: Props) {
  const speakingClassLevelOptions = getAllSpeakingClassLevelOptions();
  const { userGroups } = useAllUserGroups();
  const userGroupOptions = userGroups.map((g) => ({
    name: g.name,
    id: g.id,
  }));
  const {
    handleSubmit,
    register,
    formState: { errors, dirtyFields },
    control,
    watch,
    setValue,
  } = useForm<UserFormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      speakingLevel: SpeakingLevel.Unknown,
      shouldEnrollToAllPaidContent: true,
      activateAccount: true,
      shouldSendResetPasswordEmail: true,
      ...(defaultValues ?? {}),
    },
  });

  const selectedUserGroupId = watch("userGroupId");
  const selectedUserGroup = userGroups.find(
    (g) => g.id === (selectedUserGroupId ?? "adults"),
  );
  useEffect(() => {
    if (!showIsOnboardingLiveToBeDone || dirtyFields.isOnboardingLiveToBeDone)
      return;
    setValue(
      "isOnboardingLiveToBeDone",
      selectedUserGroup?.onboardingLiveRequired ?? false,
    );
  }, [
    showIsOnboardingLiveToBeDone,
    dirtyFields.isOnboardingLiveToBeDone,
    selectedUserGroup,
    setValue,
  ]);

  const { withLoading, loading } = useWithLoading();
  const onSubmit: SubmitHandler<UserFormInputs> = async (data) => {
    await withLoading(async () => onSave(data));
  };

  return (
    <form>
      <Col>
        <Row>
          <InputWithLabel label="Name">
            <TextInput
              disabled={disabledMap?.givenName}
              errorMessage={errors.givenName?.message}
              {...register("givenName")}
            />
          </InputWithLabel>
          <InputWithLabel label="Surname">
            <TextInput
              disabled={disabledMap?.familyName}
              errorMessage={errors.familyName?.message}
              {...register("familyName")}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <InputWithLabel label="Email">
            <TextInput
              disabled={disabledMap?.email}
              errorMessage={errors.email?.message}
              {...register("email")}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <InputWithLabel label="Hide book RLC section">
            <Controller
              render={({ field }) => {
                return (
                  <BooleanInput onChange={field.onChange} value={field.value} />
                );
              }}
              name={"hideBookSpeakingClassSection"}
              control={control}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <InputWithLabel label="Hide book Progress Check section">
            <Controller
              render={({ field }) => {
                return (
                  <BooleanInput onChange={field.onChange} value={field.value} />
                );
              }}
              name={"hideBookProgressCheckSection"}
              control={control}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <InputWithLabel label={"Speaking level"}>
            <Controller
              disabled={disabledMap?.speakingLevel}
              control={control}
              render={({ field }) => {
                return (
                  <Select<SpeakingLevel>
                    disabled={disabledMap?.speakingLevel}
                    errorMessage={errors.speakingLevel?.message}
                    onChange={field.onChange}
                    value={field.value}
                    options={speakingClassLevelOptions}
                  />
                );
              }}
              name={"speakingLevel"}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <InputWithLabel label={"User group"}>
            <Controller
              disabled={disabledMap?.userGroupId}
              control={control}
              render={({ field }) => {
                return (
                  <Select<string>
                    disabled={disabledMap?.userGroupId}
                    errorMessage={errors.userGroupId?.message}
                    onChange={field.onChange}
                    value={field.value}
                    options={userGroupOptions}
                  />
                );
              }}
              name={"userGroupId"}
            />
          </InputWithLabel>
        </Row>
        {showIsOnboardingLiveToBeDone ? (
          <Row>
            <InputWithLabel
              label={"Live onboarding da fare"}
              smallText={
                "Se attivo, l'utente dovrà partecipare a una live onboarding prima di poter usare la piattaforma (solo se il suo user group lo prevede)"
              }
            >
              <Controller
                control={control}
                render={({ field }) => (
                  <BooleanInput
                    onChange={(value) =>
                      setValue("isOnboardingLiveToBeDone", value, {
                        shouldDirty: true,
                      })
                    }
                    value={field.value}
                  />
                )}
                name={"isOnboardingLiveToBeDone"}
              />
            </InputWithLabel>
          </Row>
        ) : null}
        {showShouldEnrollToAllPaidContent ? (
          <Row>
            <InputWithLabel
              label={"Grant user access to all paid contents (Thinkific)"}
            >
              <Controller
                disabled={disabledMap?.shouldEnrollToAllPaidContent}
                control={control}
                render={({ field }) => {
                  return (
                    <BooleanInput
                      onChange={field.onChange}
                      value={field.value}
                    />
                  );
                }}
                name={"shouldEnrollToAllPaidContent"}
              />
            </InputWithLabel>
          </Row>
        ) : null}
        <Row>
          <InputWithLabel
            label={
              "Grant user access to Fluente Per Sempre speaking platform even if they didn't pay for it"
            }
          >
            <Controller
              disabled={disabledMap?.activateAccount}
              control={control}
              render={({ field }) => {
                return (
                  <BooleanInput onChange={field.onChange} value={field.value} />
                );
              }}
              name={"activateAccount"}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <InputWithLabel label={"Send Credentials"}>
            <Controller
              control={control}
              name={"shouldSendResetPasswordEmail"}
              render={({ field }) => {
                return (
                  <Select
                    errorMessage={errors.shouldSendResetPasswordEmail?.message}
                    placeholder={"Send reset password email"}
                    options={[
                      { name: "Yes", id: "true" },
                      { name: "No", id: "false" },
                    ]}
                    value={field.value ? "true" : "false"}
                    onChange={(val) => {
                      field.onChange(val === "true");
                    }}
                  />
                );
              }}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <div className={"flex w-full justify-end"}>
            <PrimaryButton
              loading={loading}
              onClick={handleSubmit(onSubmit)}
              label={"Save"}
            />
          </div>
        </Row>
      </Col>
    </form>
  );
}
